/**
 * Focus Trap Manager - Coordinates Focus Traps between different components
 * especially between CountryBar and OneTrust Consent Banner
 */

class FocusTrapManager {
  constructor() {
    this.activeTraps = new Map();
    this.oneTrustReady = false;
    this.pendingOneTrustTrap = false;
    this.focusStack = []; // Stack to track previously focused elements

    // OneTrust Event Listeners
    this.initOneTrustListeners();
  }

  /**
   * Registers a Focus Trap
   * @param {string} name - Name of the trap (e.g. 'countryBar', 'oneTrust')
   * @param {object} trap - Focus Trap object with activate/deactivate methods
   * @param {number} priority - Priority (higher number = higher priority)
   * @param {HTMLElement|null} triggerElement - Element that triggered the modal (for focus restoration)
   */
  register(name, trap, priority = 0, triggerElement = null) {
    this.activeTraps.set(name, { trap, priority, active: false, triggerElement });
  }

  /**
   * Activates a Focus Trap and deactivates others with lower priority
   * @param {string} name - Name of the trap to activate
   */
  activate(name) {
    const currentTrap = this.activeTraps.get(name);
    if (!currentTrap) {
      console.warn(`[FocusTrapManager] Trap '${name}' not registered`);
      return;
    }

    // Store the currently focused element for focus restoration
    this.storeFocusedElement(name);

    // Deactivate all other traps with lower or equal priority
    this.activeTraps.forEach((otherTrap, otherName) => {
      if (otherName !== name && otherTrap.priority <= currentTrap.priority && otherTrap.active) {
        try {
          otherTrap.trap.deactivate?.();
          otherTrap.active = false;
        } catch (error) {
          console.error(`[FocusTrapManager] Error deactivating trap ${otherName}:`, error);
        }
      }
    });

    // Activate the desired trap
    try {
      currentTrap.trap.activate?.();
      currentTrap.active = true;
    } catch (error) {
      console.error(`[FocusTrapManager] Error activating trap ${name}:`, error);
    }
  }

  /**
   * Deactivates a specific Focus Trap
   * @param {string} name - Name of the trap to deactivate
   */
  deactivate(name) {
    const trap = this.activeTraps.get(name);
    if (!trap || !trap.active) return;

    try {
      trap.trap.deactivate?.();
      trap.active = false;

      // Restore focus to the previously focused element
      this.restoreFocus(name);

      // Activate the next trap with highest priority
      this.activateHighestPriorityTrap(name);
    } catch (error) {
      console.error(`[FocusTrapManager] Error deactivating trap ${name}:`, error);
    }
  }

  /**
   * Removes a Focus Trap
   * @param {string} name - Name of the trap to remove
   */
  unregister(name) {
    const trap = this.activeTraps.get(name);
    if (trap?.active) {
      this.deactivate(name);
    }
    this.activeTraps.delete(name);
  }

  /**
   * Stores the currently focused element before activating a trap
   * @param {string} trapName - Name of the trap being activated
   */
  storeFocusedElement(trapName) {
    const trap = this.activeTraps.get(trapName);
    const activeElement = document.activeElement;

    // Use the triggerElement if provided, otherwise use current activeElement
    const elementToStore = trap?.triggerElement || activeElement;

    // Only store valid focusable elements (not body or html)
    if (
      elementToStore &&
      elementToStore !== document.body &&
      elementToStore !== document.documentElement &&
      typeof elementToStore.focus === 'function'
    ) {
      this.focusStack.push({ trapName, element: elementToStore });
    }
  }

  /**
   * Restores focus to the previously focused element
   * @param {string} trapName - Name of the trap being deactivated
   */
  restoreFocus(trapName) {
    // Find the most recent entry for this trap
    for (let i = this.focusStack.length - 1; i >= 0; i--) {
      const entry = this.focusStack[i];
      if (entry.trapName === trapName) {
        // Remove this entry from the stack
        this.focusStack.splice(i, 1);

        // Restore focus if the element is still in the DOM and focusable
        if (entry.element && document.contains(entry.element) && typeof entry.element.focus === 'function') {
          // Use nextTick equivalent to ensure DOM is stable
          setTimeout(() => {
            try {
              entry.element.focus();
            } catch (error) {
              console.warn(`[FocusTrapManager] Could not restore focus to element:`, error);
            }
          }, 0);
        }
        break;
      }
    }
  }

  /**
   * Updates the trigger element for an existing trap
   * @param {string} name - Name of the trap
   * @param {HTMLElement} triggerElement - New trigger element
   */
  updateTriggerElement(name, triggerElement) {
    const trap = this.activeTraps.get(name);
    if (trap) {
      trap.triggerElement = triggerElement;
    }
  }

  /**
   * Activates the trap with the highest priority (except the excluded one)
   * @param {string} excludeName - Name of the trap to ignore
   */
  activateHighestPriorityTrap(excludeName = null) {
    let highestPriorityTrap = null;
    let highestPriority = -1;
    let highestPriorityName = null;

    this.activeTraps.forEach((trap, name) => {
      if (name !== excludeName && trap.priority > highestPriority && !trap.active) {
        highestPriority = trap.priority;
        highestPriorityTrap = trap;
        highestPriorityName = name;
      }
    });

    if (highestPriorityTrap && highestPriorityName) {
      this.activate(highestPriorityName);
    }
  }

  /**
   * Initializes OneTrust Event Listeners
   */
  initOneTrustListeners() {
    if (typeof window === 'undefined') return;

    // Wait for OneTrust Load
    const checkOneTrust = () => {
      if (window.OneTrust && window.OneTrust.IsAlertBoxClosed !== undefined) {
        this.oneTrustReady = true;
        this.setupOneTrustTrap();
      } else {
        setTimeout(checkOneTrust, 100);
      }
    };

    checkOneTrust();

    // Listen to OneTrust Events
    document.addEventListener('OneTrustLoaded', () => {
      this.oneTrustReady = true;
      this.setupOneTrustTrap();
    });

    // Listen to banner show/hide events
    document.addEventListener('OneTrustGroupsUpdated', () => {
      // If consent was given, deactivate OneTrust Trap
      setTimeout(() => {
        if (window.OneTrust?.IsAlertBoxClosed()) {
          this.deactivate('oneTrust');
        }
      }, 100);
    });
  }

  /**
   * Setup OneTrust Focus Trap
   */
  setupOneTrustTrap() {
    if (!this.oneTrustReady || this.activeTraps.has('oneTrust')) return;

    const checkForBanner = () => {
      const banner = document.querySelector('#onetrust-banner-sdk');
      if (banner && banner.style.display !== 'none') {
        this.createOneTrustTrap(banner);
      }
    };

    // Immediate check
    checkForBanner();

    // Observer for OneTrust Banner
    const observer = new MutationObserver(() => {
      checkForBanner();
    });

    // Observe body for OneTrust Banner Changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
  }

  /**
   * Creates Focus Trap for OneTrust Banner
   * @param {HTMLElement} banner - OneTrust Banner Element
   */
  createOneTrustTrap(banner) {
    if (this.activeTraps.has('oneTrust')) return;

    let focusableElements = [];
    let firstFocusableElement = null;
    let lastFocusableElement = null;

    const updateFocusableElements = () => {
      focusableElements = banner.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      firstFocusableElement = focusableElements[0];
      lastFocusableElement = focusableElements[focusableElements.length - 1];
    };

    const handleKeyDown = (e) => {
      if (e.key !== 'Tab') return;

      updateFocusableElements();

      if (e.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          lastFocusableElement?.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusableElement) {
          firstFocusableElement?.focus();
          e.preventDefault();
        }
      }
    };

    const oneTrustTrap = {
      activate: () => {
        updateFocusableElements();
        banner.addEventListener('keydown', handleKeyDown);
        // Focus first element
        setTimeout(() => {
          firstFocusableElement?.focus();
        }, 0);
      },
      deactivate: () => {
        banner.removeEventListener('keydown', handleKeyDown);
      },
    };

    // Register OneTrust Trap with highest priority
    this.register('oneTrust', oneTrustTrap, 100);

    // Activate immediately if banner is visible
    const isVisible =
      banner.offsetParent !== null &&
      window.getComputedStyle(banner).display !== 'none' &&
      window.getComputedStyle(banner).visibility !== 'hidden';

    if (isVisible) {
      setTimeout(() => {
        this.activate('oneTrust');
      }, 100);
    }
  }

  /**
   * Checks if OneTrust Banner is visible
   * @returns {boolean}
   */
  isOneTrustBannerVisible() {
    const banner = document.querySelector('#onetrust-banner-sdk');
    if (!banner) return false;

    return (
      banner.offsetParent !== null &&
      window.getComputedStyle(banner).display !== 'none' &&
      window.getComputedStyle(banner).visibility !== 'hidden'
    );
  }
}

// Singleton Instance
let focusTrapManager = null;

export const useFocusTrapManager = () => {
  if (!focusTrapManager && typeof window !== 'undefined') {
    focusTrapManager = new FocusTrapManager();
  }
  return focusTrapManager;
};

export default useFocusTrapManager;
